import type { ProjectStats } from '../types/api';

interface ProjectOverviewProps {
  stats: ProjectStats[] | null;
  loading: boolean;
  error: string | null;
  selectedProject: string;
  onProjectSelect: (projectName: string) => void;
}

export function ProjectOverview({
  stats,
  loading,
  error,
  selectedProject,
  onProjectSelect,
}: ProjectOverviewProps) {
  if (loading) {
    return (
      <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-primary-900/30">
        <div className="animate-pulse">
          <div className="h-8 bg-dark-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-dark-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-red-500/30">
        <div className="text-red-500 flex items-center gap-2">
          <span className="text-2xl">⚠️</span>
          <span className="font-semibold">Ошибка загрузки статистики: {error}</span>
        </div>
      </div>
    );
  }

  if (!stats || stats.length === 0) {
    return (
      <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-dark-200">
        <div className="text-dark-500 text-center py-8">
          <span className="text-4xl mb-2 block">📊</span>
          <p>Нет данных для отображения</p>
        </div>
      </div>
    );
  }

  // Если выбран конкретный проект - показываем карточки
  if (selectedProject !== 'all') {
    const stat = stats.find((s) => s.extensionName === selectedProject);
    
    if (!stat) {
      return (
        <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-dark-200">
          <div className="text-dark-500 text-center py-8">
            <span className="text-4xl mb-2 block">🔍</span>
            <p>Проект не найден</p>
          </div>
        </div>
      );
    }

    const uptimeColor =
      stat.uptimePercent >= 99
        ? 'text-green-400'
        : stat.uptimePercent >= 95
        ? 'text-yellow-400'
        : 'text-red-400';

    return (
      <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-primary-900/30">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary-400 flex items-center gap-2">
            <span className="text-3xl">📦</span>
            Детали проекта: <span className="text-primary-300">{stat.extensionName}</span>
          </h2>
          <button
            onClick={() => onProjectSelect('all')}
            className="text-sm text-primary-400 hover:text-primary-300 underline transition-colors"
          >
            ← Вернуться ко всем проектам
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Uptime */}
          <div className="bg-gradient-to-br from-dark-50 to-dark-100 rounded-lg p-6 border border-dark-200 hover:border-primary-600 transition-all hover:shadow-lg hover:shadow-primary-900/20">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-dark-500 font-semibold uppercase tracking-wide">
                Uptime
              </div>
              <div className="text-2xl">
                {stat.uptimePercent >= 99 ? '🟢' : stat.uptimePercent >= 95 ? '🟡' : '🔴'}
              </div>
            </div>
            <div className={`text-5xl font-bold ${uptimeColor} mb-2`}>
              {stat.uptimePercent.toFixed(1)}%
            </div>
            <div className="text-xs text-dark-400">
              {stat.uptimePercent >= 99
                ? 'Отличная стабильность'
                : stat.uptimePercent >= 95
                ? 'Нормальная работа'
                : 'Требуется внимание'}
            </div>
          </div>

          {/* Total Events */}
          <div className="bg-gradient-to-br from-dark-50 to-dark-100 rounded-lg p-6 border border-dark-200 hover:border-primary-600 transition-all hover:shadow-lg hover:shadow-primary-900/20">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-dark-500 font-semibold uppercase tracking-wide">
                Всего событий
              </div>
              <div className="text-2xl">📊</div>
            </div>
            <div className="text-5xl font-bold text-primary-400 mb-2">
              {stat.total.toLocaleString()}
            </div>
            <div className="text-xs text-dark-400">за выбранный период</div>
          </div>

          {/* Errors */}
          <div className="bg-gradient-to-br from-dark-50 to-dark-100 rounded-lg p-6 border border-dark-200 hover:border-red-600 transition-all hover:shadow-lg hover:shadow-red-900/20">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-dark-500 font-semibold uppercase tracking-wide">
                Ошибки
              </div>
              <div className="text-2xl">🔴</div>
            </div>
            <div className="text-5xl font-bold text-red-400 mb-2">
              {stat.errorCount.toLocaleString()}
            </div>
            <div className="text-xs text-dark-400">
              {((stat.errorCount / stat.total) * 100).toFixed(2)}% от общего числа
            </div>
          </div>

          {/* Success */}
          <div className="bg-gradient-to-br from-dark-50 to-dark-100 rounded-lg p-6 border border-dark-200 hover:border-green-600 transition-all hover:shadow-lg hover:shadow-green-900/20">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-dark-500 font-semibold uppercase tracking-wide">
                Успешных
              </div>
              <div className="text-2xl">✅</div>
            </div>
            <div className="text-5xl font-bold text-green-400 mb-2">
              {stat.successCount.toLocaleString()}
            </div>
            <div className="text-xs text-dark-400">
              {((stat.successCount / stat.total) * 100).toFixed(2)}% от общего числа
            </div>
          </div>
        </div>

        {/* Дополнительная информация по IP */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-dark-50 rounded-lg p-4 border border-dark-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-dark-500 mb-1">Уникальных IP адресов</div>
                <div className="text-3xl font-bold text-primary-400">{stat.ips.totalIps}</div>
              </div>
              <div className="text-4xl">🌐</div>
            </div>
          </div>

          <div className="bg-dark-50 rounded-lg p-4 border border-dark-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-dark-500 mb-1">Аномальных IP</div>
                <div className="text-3xl font-bold text-red-400">
                  {stat.ips.topIps.filter((ip) => ip.isAnomaly).length}
                </div>
              </div>
              <div className="text-4xl">🚨</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Если выбраны все проекты - показываем таблицу
  return (
    <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-primary-900/30">
      <h2 className="text-2xl font-bold text-primary-400 mb-6 flex items-center gap-2">
        <span className="text-3xl">📊</span>
        Обзор всех проектов
        <span className="text-lg text-dark-500 font-normal ml-2">({stats.length} проектов)</span>
      </h2>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-dark-200">
              <th className="text-left py-3 px-4 text-dark-600 font-semibold">Проект</th>
              <th className="text-left py-3 px-4 text-dark-600 font-semibold">Uptime</th>
              <th className="text-left py-3 px-4 text-dark-600 font-semibold">Всего</th>
              <th className="text-left py-3 px-4 text-dark-600 font-semibold">Ошибок</th>
              <th className="text-left py-3 px-4 text-dark-600 font-semibold">Успешных</th>
              <th className="text-left py-3 px-4 text-dark-600 font-semibold">IP адресов</th>
              <th className="text-left py-3 px-4 text-dark-600 font-semibold">Аномалий</th>
            </tr>
          </thead>
          <tbody>
            {stats
              .sort((a, b) => a.uptimePercent - b.uptimePercent) // Сортируем по uptime (низкие первыми)
              .map((stat) => {
                const uptimeColor =
                  stat.uptimePercent >= 99
                    ? 'text-green-400'
                    : stat.uptimePercent >= 95
                    ? 'text-yellow-400'
                    : 'text-red-400';
                const anomalyCount = stat.ips.topIps.filter((ip) => ip.isAnomaly).length;
                const isLowUptime = stat.uptimePercent < 95;

                return (
                  <tr
                    key={stat.extensionName}
                    onClick={() => onProjectSelect(stat.extensionName)}
                    className={`border-b border-dark-200 hover:bg-dark-50 transition-all cursor-pointer ${
                      isLowUptime ? 'bg-red-900/10 hover:bg-red-900/20' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {isLowUptime && <span className="text-red-400">⚠️</span>}
                        <span className="font-semibold text-primary-300">
                          {stat.extensionName}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-bold text-lg ${uptimeColor}`}>
                        {stat.uptimePercent.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-dark-700 font-semibold">
                      {stat.total.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-red-400 font-semibold">{stat.errorCount}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-green-400 font-semibold">{stat.successCount}</span>
                    </td>
                    <td className="py-3 px-4 text-dark-700">{stat.ips.totalIps}</td>
                    <td className="py-3 px-4">
                      {anomalyCount > 0 ? (
                        <span className="text-orange-400 font-semibold">⚠️ {anomalyCount}</span>
                      ) : (
                        <span className="text-dark-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-4 bg-dark-50 rounded-lg border border-dark-200">
        <p className="text-sm text-dark-600">
          <span className="font-semibold text-primary-400">💡 Подсказка:</span>{' '}
          Нажмите на строку, чтобы увидеть подробную информацию о проекте.
          Проекты с низким uptime выделены красным.
        </p>
      </div>
    </div>
  );
}

